/* eslint-disable class-methods-use-this */
import { ZeroAddress } from 'ethers'

import ErrorHumanizerError from '../../classes/ErrorHumanizerError'
import { IAccountsController } from '../../interfaces/account'
import { IActivityController } from '../../interfaces/activity'
import { ErrorRef } from '../../interfaces/eventEmitter'
import { IKeystoreController } from '../../interfaces/keystore'
import { INetworksController } from '../../interfaces/network'
import { IPortfolioController } from '../../interfaces/portfolio'
import { RPCProvider } from '../../interfaces/provider'
import { SignAccountOpError, Warning } from '../../interfaces/signAccountOp'
import { getAccountNetworkNotSupportedReason, isSmartAccount } from '../../libs/account/account'
import { BaseAccount } from '../../libs/account/BaseAccount'
import { getBaseAccount } from '../../libs/account/getBaseAccount'
import { AccountOp } from '../../libs/accountOp/accountOp'
import { getEstimation, getEstimationSummary } from '../../libs/estimate/estimate'
import {
  FeePaymentOption,
  FullEstimationSummary,
  ProviderEstimation
} from '../../libs/estimate/interfaces'
import { isPortfolioGasTankResult } from '../../libs/portfolio/helpers'
import { TokenResult } from '../../libs/portfolio'
import { BundlerSwitcher } from '../../services/bundlers/bundlerSwitcher'
import { getIsViewOnly } from '../../utils/accounts'
import EventEmitter from '../eventEmitter/eventEmitter'
import { EstimationStatus } from './types'

/**
 * When the wallet cannot simulate a transaction because a previous transaction
 * from the same account is still pending on-chain (the most common cause is
 * the dapp ERC20 approve → swap pattern on accounts that cannot batch — e.g.
 * SafePal HW imports), we fall back to a high gas limit so the user can still
 * sign and broadcast. The transaction queues in the mempool with the next
 * sequential nonce and executes after the pending tx confirms.
 *
 * 800k is the same ballpark MetaMask uses as a fallback when its estimate
 * fails — high enough to cover most ERC20 swap calls without OOG.
 */
const WAITING_FOR_PREVIOUS_TX_FALLBACK_GAS_USED = 800000n

export class EstimationController extends EventEmitter {
  #keystore: IKeystoreController

  #accounts: IAccountsController

  #networks: INetworksController

  #provider: RPCProvider

  #portfolio: IPortfolioController

  status: EstimationStatus = EstimationStatus.Initial

  estimation: FullEstimationSummary | null = null

  error: Error | null = null

  /**
   * a boolean to understand if the estimation has been performed
   * at least one indicating clearly that all other are re-estimates
   */
  hasEstimated: boolean = false

  estimationRetryError: ErrorRef | null = null

  availableFeeOptions: FeePaymentOption[] = []

  #bundlerSwitcher: BundlerSwitcher

  #notFatalBundlerError?: Error

  #activity: IActivityController

  /**
   * Used to prevent slow estimations for a past accountOp overwriting
   * the latest estimation results
   */
  private lastAccountOpId: string | null = null

  /**
   * Snapshot of the last accountOp we estimated. Used by the activity listener
   * so that when a pending tx on the same (account, chainId) confirms, we can
   * re-run estimation immediately instead of waiting for the next scheduled tick.
   */
  #currentOp: AccountOp | null = null

  /**
   * Number of pending submitted ops for (#currentOp.accountAddr, chainId)
   * seen during the last estimation. We trigger an immediate retry when this
   * count decreases (i.e. a previous tx confirmed / was dropped).
   */
  #lastPendingOpsCount: number = 0

  #unsubscribeFromActivity: (() => void) | null = null

  /**
   * True when the most recent estimation reverted with the "Inner call: 0x"
   * pattern AND there is at least one pending submitted op on the same
   * (account, chain). In that case we substitute a fallback gas limit so the
   * user can still sign — the tx will queue in the mempool behind the pending
   * one. The UI uses this flag to show an info banner explaining the wait.
   */
  isUsingFallbackEstimationDueToPendingTx: boolean = false

  constructor(
    keystore: IKeystoreController,
    accounts: IAccountsController,
    networks: INetworksController,
    provider: RPCProvider,
    portfolio: IPortfolioController,
    bundlerSwitcher: BundlerSwitcher,
    activity: IActivityController
  ) {
    super()
    this.#keystore = keystore
    this.#accounts = accounts
    this.#networks = networks
    this.#provider = provider
    this.#portfolio = portfolio
    this.#bundlerSwitcher = bundlerSwitcher
    this.#activity = activity

    // When activity reports a status change (e.g. a pending tx confirmed),
    // and we are currently in fallback-estimation mode (waiting on a previous
    // tx), re-estimate immediately so the real estimate replaces the fallback
    // as soon as possible. Activity polls receipts every 5s, so this hook
    // gives us the fastest possible unblock without waiting for our own
    // scheduled tick.
    this.#unsubscribeFromActivity = this.#activity.onUpdate(() => {
      if (!this.#currentOp) return
      const pendingCount = this.#countPendingOpsForCurrent()
      const decreased = pendingCount < this.#lastPendingOpsCount
      this.#lastPendingOpsCount = pendingCount
      if (!decreased) return
      if (!this.isUsingFallbackEstimationDueToPendingTx) return
      // Fire-and-forget; estimate() internally guards against races.
      this.estimate(this.#currentOp).catch(() => {})
    }, 'estimation-waiting-for-prev-tx')
  }

  /**
   * Returns the number of broadcasted-but-not-confirmed ops that live on the
   * same (account, chain) as the op we are currently estimating.
   */
  #countPendingOpsForCurrent(): number {
    if (!this.#currentOp) return 0
    const opsForAccount = this.#activity.broadcastedButNotConfirmed[this.#currentOp.accountAddr]
    if (!opsForAccount) return 0
    return opsForAccount.filter((op) => op.chainId === this.#currentOp!.chainId).length
  }

  /**
   * Decides whether an estimation failure should fall back to a high gas
   * limit instead of blocking the user from signing.
   *
   * The condition: the failure looks like the "Inner call: 0x" pattern (raw
   * revert with empty return data) AND there is at least one pending submitted
   * op on the same (account, chain). The canonical case is a dapp that just
   * requested an ERC20 approval and immediately asked to sign a second tx
   * whose simulation depends on that allowance. On EOAs that cannot batch
   * (e.g. SafePal HW imports), the two calls run as separate sequential txs,
   * so the second simulation necessarily reverts until the approval mines.
   *
   * Mirrors the MetaMask flow: show a soft warning, let the user sign, and
   * let the mempool execute the txs in nonce order.
   */
  #shouldUseFallbackForPendingTx(failureError: Error | null): boolean {
    if (!failureError) return false
    const cause =
      failureError instanceof ErrorHumanizerError
        ? (failureError.cause as string | null)
        : null
    if (cause !== 'Inner call: 0x') return false
    return this.#countPendingOpsForCurrent() > 0
  }

  /**
   * Builds a synthetic FullEstimationSummary containing only a
   * ProviderEstimation with a high fallback gas limit and the native fee
   * payment option. Used when #shouldUseFallbackForPendingTx() is true.
   *
   * We do not synthesise an Ambire/Bundler estimation because the fallback
   * path only matters for accounts that cannot batch (i.e. EOA broadcasting
   * via the provider path).
   */
  #buildFallbackEstimationSummary(
    accountAddr: string,
    accountBalance: bigint,
    nativeFeeToken: TokenResult
  ): FullEstimationSummary {
    const fallbackProviderEstimation: ProviderEstimation = {
      gasUsed: WAITING_FOR_PREVIOUS_TX_FALLBACK_GAS_USED,
      feePaymentOptions: [
        {
          paidBy: accountAddr,
          availableAmount: accountBalance,
          addedNative: 0n,
          token: nativeFeeToken,
          gasUsed: 0n
        }
      ]
    }

    return {
      providerEstimation: fallbackProviderEstimation,
      ambireEstimation: undefined,
      bundlerEstimation: undefined,
      bundlerGasPrices: undefined,
      flags: {},
      updatedAt: Date.now()
    }
  }

  #getAvailableFeeOptions(baseAcc: BaseAccount, op: AccountOp): FeePaymentOption[] {
    const estimation = this.estimation as FullEstimationSummary

    const feeOptions = baseAcc.getAvailableFeeOptions(
      estimation,
      // eslint-disable-next-line no-nested-ternary
      estimation.ambireEstimation
        ? estimation.ambireEstimation.feePaymentOptions
        : estimation.providerEstimation
          ? estimation.providerEstimation.feePaymentOptions
          : [],
      op
    )

    // ZKX: never offer the Gas Tank as a fee option for a Gas Tank top-up. The
    // deposit only credits the tank asynchronously (after the on-chain transfer
    // to FEE_COLLECTOR confirms), so on a first/empty top-up there is nothing to
    // spend and the relayer rejects it with "Invalid payment option". Removing
    // the on-gas-tank options here hides them from the "Pay fee with" list and
    // makes the default selection fall back to a wallet token. Wallet-token
    // options always remain, so `availableFeeOptions` is never emptied.
    if (op.meta?.topUpAmount) {
      return feeOptions.filter((option) => !option.token.flags.onGasTank)
    }

    return feeOptions
  }

  async estimate(op: AccountOp) {
    this.status = EstimationStatus.Loading
    // Track the latest op so the activity listener can retry us for this exact
    // (account, chain) context. This must be set before any await, otherwise
    // an activity update that fires during the estimate may be dropped.
    this.#currentOp = op
    this.#lastPendingOpsCount = this.#countPendingOpsForCurrent()
    this.emitUpdate()

    const account = this.#accounts.accounts.find((acc) => acc.addr === op.accountAddr)!
    const network = this.#networks.networks.find((net) => net.chainId === op.chainId)!
    const accountState = await this.#accounts.getOrFetchAccountOnChainState(
      op.accountAddr,
      op.chainId
    )
    if (!accountState) {
      this.error = new Error(
        'During the preparation step, required transaction information was found missing (account state). Please try again later or contact support.'
      )
      this.status = EstimationStatus.Error
      this.hasEstimated = true
      this.emitUpdate()
      return
    }

    // Second line of defence behind the dApp-request guard in RequestsController:
    // whatever queued this op, an account that cannot exist on this chain can
    // only fail. Estimating anyway calls the deployless estimator against an
    // undeployed factory, which reverts as a bare CALL_EXCEPTION and humanizes
    // into "unknown error (Origin: RPC call) … contact ZKX support" — telling
    // the user to ask support about a permanent, known limitation. Say the real
    // reason instead. ErrorHumanizerError so the sign screen renders the message
    // as-is rather than re-humanizing it.
    const networkNotSupportedReason = getAccountNetworkNotSupportedReason(
      account,
      network,
      accountState
    )
    if (networkNotSupportedReason) {
      this.error = new ErrorHumanizerError(networkNotSupportedReason, {
        cause: 'account-network-not-supported',
        isFallbackMessage: false
      })
      this.status = EstimationStatus.Error
      this.hasEstimated = true
      this.emitUpdate()
      return
    }

    const baseAcc = getBaseAccount(account, accountState, network)

    // Take the fee tokens from two places: the user's tokens and his gasTank
    // The gasTank tokens participate on each network as they belong everywhere
    // NOTE: at some point we should check all the "?" signs below and if
    // an error pops out, we should notify the user about it
    let networkFeeTokens =
      this.#portfolio.getAccountPortfolioState(op.accountAddr)?.[op.chainId.toString()]?.result
        ?.feeTokens ?? []

    // This could happen only in a race when a NOT currently selected account is
    // requested, switched to and immediately fired a txn request for. In that situation,
    // the portfolio would not be fetched and the estimation would be fired without tokens,
    // resulting in a "nothing to pay the fee with" error which is absolutely wrong
    if (networkFeeTokens.length === 0) {
      await this.#portfolio.updateSelectedAccount(op.accountAddr, [network])
      networkFeeTokens =
        this.#portfolio.getAccountPortfolioState(op.accountAddr)?.[op.chainId.toString()]?.result
          ?.feeTokens ?? []
    }

    const gasTankResult = this.#portfolio.getAccountPortfolioState(op.accountAddr)?.gasTank?.result
    const gasTankFeeTokens = isPortfolioGasTankResult(gasTankResult)
      ? gasTankResult.gasTankTokens
      : []
    const feeTokens =
      [...networkFeeTokens, ...gasTankFeeTokens].filter((t) => t.flags.isFeeToken) || []

    // Here, we list EOA accounts for which you can also obtain an estimation of the AccountOp payment.
    // In the case of operating with a smart account (an account with creation code), all other EOAs can pay the fee.
    //
    // If the current account is an EOA, only this account can pay the fee,
    // and there's no need for checking other EOA accounts native balances.
    // This is already handled and estimated as a fee option in the estimate library, which is why we pass an empty array here.
    //
    // we're excluding the view only accounts from the natives to check
    // in all cases EXCEPT the case where we're making an estimation for
    // the view only account itself. In all other, view only accounts options
    // should not be present as the user cannot pay the fee with them (no key)
    const nativeToCheck = baseAcc.canBroadcastByOtherEOA()
      ? this.#accounts.accounts
          .filter(
            (acc) =>
              !isSmartAccount(acc) &&
              (acc.addr === op.accountAddr ||
                !getIsViewOnly(this.#keystore.keys, acc.associatedKeys))
          )
          // internal keys first
          .sort((a, b) => {
            const aKeyInternal = this.#keystore.keys.find(
              (k) => k.type === 'internal' && k.addr === a.addr
            )
            const bKeyInternal = this.#keystore.keys.find(
              (k) => k.type === 'internal' && k.addr === b.addr
            )
            if (aKeyInternal && !bKeyInternal) return -1
            if (!aKeyInternal && bKeyInternal) return 1
            return 0
          })
          .map((acc) => acc.addr)
      : []

    this.lastAccountOpId = op.id

    const estimation = await getEstimation(
      baseAcc,
      accountState,
      op,
      network,
      this.#provider,
      feeTokens,
      nativeToCheck,
      this.#bundlerSwitcher,
      (this.#activity.broadcastedButNotConfirmed[account.addr] || []).find(
        (accOp) => accOp.chainId === network.chainId && !!accOp.asUserOperation
      )
    ).catch((e) => {
      console.error(e)
      return e
    })

    // Done to prevent race conditions
    if (op.id !== this.lastAccountOpId) {
      const error = new Error(
        `Estimation race condition prevented. Op id: ${op.id}. Expected: ${this.lastAccountOpId}`
      )

      this.emitError({
        message: 'Estimation race condition prevented',
        error,
        level: 'silent'
      })
      return
    }

    const isSuccess = !(estimation instanceof Error) && !estimation.criticalError
    if (isSuccess) {
      this.estimation = getEstimationSummary(estimation)
      this.error = null
      this.status = EstimationStatus.Success
      this.estimationRetryError = null
      this.availableFeeOptions = this.#getAvailableFeeOptions(baseAcc, op)
      this.#notFatalBundlerError =
        estimation.bundler instanceof Error
          ? new Error(estimation.bundler.message, { cause: '4337_ESTIMATION' })
          : undefined
      this.isUsingFallbackEstimationDueToPendingTx = false
    } else {
      const failureError =
        estimation instanceof Error ? estimation : estimation.criticalError ?? null
      const nativeFeeToken = feeTokens.find(
        (token) => token.address === ZeroAddress && !token.flags.onGasTank
      )

      // MetaMask-style fallback: if the only reason estimation failed is that
      // a previous tx from the same account is still pending (so the simulated
      // call sees stale state — typically an unset ERC20 allowance), treat the
      // estimation as a soft success with a high fallback gas limit. The user
      // can sign; the tx will queue in the mempool and execute after the
      // pending one mines.
      if (
        nativeFeeToken &&
        !isSmartAccount(account) &&
        this.#shouldUseFallbackForPendingTx(failureError)
      ) {
        this.estimation = this.#buildFallbackEstimationSummary(
          account.addr,
          accountState.balance,
          nativeFeeToken
        )
        this.error = null
        this.status = EstimationStatus.Success
        this.estimationRetryError = null
        this.availableFeeOptions = this.#getAvailableFeeOptions(baseAcc, op)
        this.#notFatalBundlerError = undefined
        this.isUsingFallbackEstimationDueToPendingTx = true
      } else {
        this.estimation = null
        this.error = failureError
        this.status = EstimationStatus.Error
        this.availableFeeOptions = []
        this.isUsingFallbackEstimationDueToPendingTx = false
      }
    }

    // estimation.flags.hasNonceDiscrepancy is a signal from the estimation
    // that the account state is not the latest and needs to be updated
    if (
      this.estimation &&
      (this.estimation.flags.hasNonceDiscrepancy || this.estimation.flags.has4337NonceDiscrepancy)
    ) {
      // continue on error here as the flags are more like app helpers
      this.#accounts
        .updateAccountState(op.accountAddr, 'pending', [op.chainId])
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e))
    }

    this.hasEstimated = true
    this.emitUpdate()
  }

  /**
   * it's initialized if it has estimated at least once
   */
  isInitialized() {
    return this.hasEstimated
  }

  /**
   * has it estimated at least once without a failure
   */
  isLoadingOrFailed(): boolean {
    return this.status === EstimationStatus.Loading || this.error instanceof Error
  }

  calculateWarnings() {
    const warnings: Warning[] = []

    if (this.estimationRetryError && this.status === EstimationStatus.Success) {
      warnings.push({
        id: 'estimation-retry',
        title: this.estimationRetryError.message,
        text: 'You can proceed, but fee estimation is outdated - consider waiting for an updated estimation for a more optimal fee.'
      })
    }

    if (this.#notFatalBundlerError?.cause === '4337_ESTIMATION') {
      warnings.push({
        id: 'bundler-failure',
        title: 'Fee options are temporarily limited',
        text: 'Use the "Retry" button to try again, or pay the fee with the native token"'
      })
    }

    if (this.#notFatalBundlerError?.cause === '4337_INVALID_NONCE') {
      warnings.push({
        id: 'bundler-nonce-discrepancy',
        title:
          'You can proceed safely, but fee payment options are limited due to a pending transaction'
      })
    }

    return warnings
  }

  get errors(): SignAccountOpError[] {
    const errors: SignAccountOpError[] = []

    if (this.isLoadingOrFailed() && this.estimationRetryError) {
      // If there is a successful estimation we should show this as a warning
      // as the user can use the old estimation to broadcast
      errors.push({
        title: `${this.estimationRetryError.message} ${
          this.error
            ? 'We will continue retrying, but please check your internet connection.'
            : 'Automatically retrying in a few seconds. Please wait...'
        }`
      })

      return errors
    }

    if (!this.isInitialized()) return []

    if (this.error) {
      let code = ''

      if (this.error instanceof ErrorHumanizerError && this.error.isFallbackMessage) {
        code =
          typeof this.error.cause === 'string' && !!this.error.cause
            ? this.error.cause
            : 'ESTIMATION_ERROR'
      }

      errors.push({
        title: this.error.message,
        code
      })
    }

    return errors
  }

  destroy() {
    if (this.#unsubscribeFromActivity) {
      this.#unsubscribeFromActivity()
      this.#unsubscribeFromActivity = null
    }
    super.destroy()
  }
}
